<div class="deal-type-header">
  <span class="deal-type-header__title">Closed Deal</span>
  <button
    mat-icon-button
    mat-dialog-close
    class="deal-type-header__close"
    aria-label="Close">
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content class="closed-deal-content">
  @if (showExtractionBanner()) {
    <div class="extraction-banner">
      <div class="extraction-banner__icon">
        <mat-icon>auto_awesome</mat-icon>
      </div>
      <div class="extraction-banner__text">
        <strong>{{ extractedFieldCount() }} fields</strong> were detected and
        pre-filled for you. Please scroll through to validate what we found and
        fill out what was not available.
      </div>
      <button
        mat-icon-button
        class="extraction-banner__close"
        (click)="dismissExtractionBanner()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  <mat-stepper
    linear
    orientation="horizontal"
    labelPosition="bottom"
    #stepper
    (selectionChange)="onStepChange($event)">
    <!-- ═══ Step 1: Setup ═══ -->
    <mat-step [stepControl]="setupForm" label="Setup">
      <form [formGroup]="setupForm" class="step-form">
        <div class="form-group">
          <label class="form-label">Does this deal have sub-facilities?</label>
          <mat-radio-group formControlName="hasSubFacilities">
            <mat-radio-button [value]="false">No</mat-radio-button>
            <mat-radio-button [value]="true" class="ml-16"
              >Yes</mat-radio-button
            >
          </mat-radio-group>
        </div>

        @if (hasSubFacilities()) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>How many sub-facilities?</mat-label>
            <mat-select formControlName="numSubFacilities">
              @for (n of subFacilityCountOptions; track n) {
                <mat-option [value]="n">{{ n }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Transaction Type</mat-label>
            <mat-select formControlName="transactionType">
              @for (t of (syndicationTypes$ | async) ?? []; track t.id) {
                <mat-option [value]="t.id">{{
                  t.description ?? t.label ?? t.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Loan Purpose</mat-label>
            <mat-select formControlName="loanPurpose">
              @for (p of (loanPurposes$ | async) ?? []; track p.id) {
                <mat-option [value]="p.id">{{
                  p.description ?? p.label ?? p.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Deal Name</mat-label>
          <input matInput formControlName="dealName" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label># Lenders</mat-label>
          <input matInput type="number" formControlName="numLenders" min="1" />
        </mat-form-field>
      </form>
      <form [formGroup]="ndaForm" class="step-form--separate">
        @let ndaWorkflowControl = ndaForm.get('workflow');
        <mat-form-field
          appearance="outline"
          [class.mat-form-field-invalid]="
            ndaWorkflowControl.invalid &&
            (ndaWorkflowControl.touched || ndaWorkflowControl.dirty)
          ">
          <mat-label>{{ 'GNR_NDA_WORKFLOW' | translate }}</mat-label>
          <mat-select formControlName="workflow">
            @for (option of ndaWorkflowOptions; track option.value) {
              <mat-option [value]="option.value">
                {{ option.label | translate }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (showNdaTemplates()) {
          <qt-template-selector
            [templateType]="templateTypes.RTF"
            [label]="'GNR_TEMPLATE'"
            [required]="true"
            formControlName="template">
          </qt-template-selector>
        }

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button
            type="button"
            mat-flat-button
            matStepperNext
            [disabled]="setupForm.invalid || ndaForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══ Step 2: Structure ═══ -->
    <mat-step [stepControl]="structureForm" label="Structure">
      <form [formGroup]="structureForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Product Type</mat-label>
            <mat-select formControlName="productType">
              @for (p of (productTypes$ | async) ?? []; track p.id) {
                <mat-option [value]="p.id">{{
                  p.description ?? p.label ?? p.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Interest Rate Type</mat-label>
            <mat-select formControlName="rateType">
              @for (r of (rateTypes$ | async) ?? []; track r.id) {
                <mat-option [value]="r.id">{{
                  r.description ?? r.label ?? r.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width benchmark-field">
          <mat-label>Original Loan Amount</mat-label>
          <input matInput type="number" formControlName="amount" min="0" />
          <span matTextPrefix>$&nbsp;</span>
        </mat-form-field>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Term (months)</mat-label>
            <input matInput type="number" formControlName="term" min="1" />
          </mat-form-field>

          @if (showAmortization()) {
            <mat-form-field appearance="outline">
              <mat-label>Amortization (months)</mat-label>
              <input
                matInput
                type="number"
                formControlName="amortization"
                min="0" />
            </mat-form-field>
          }
        </div>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Closed Date</mat-label>
            <input
              matInput
              [matDatepicker]="closedPicker"
              formControlName="closedDate" />
            <mat-datepicker-toggle
              matSuffix
              [for]="closedPicker"></mat-datepicker-toggle>
            <mat-datepicker #closedPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>Maturity Date</mat-label>
            <input
              matInput
              [matDatepicker]="maturityPicker"
              formControlName="maturityDate" />
            <mat-datepicker-toggle
              matSuffix
              [for]="maturityPicker"></mat-datepicker-toggle>
            <mat-datepicker #maturityPicker></mat-datepicker>
            <mat-hint>Auto-calculated from closed date + term</mat-hint>
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            type="button"
            mat-flat-button
            matStepperNext
            [disabled]="structureForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══ Step 3: Borrower ═══ -->
    <mat-step [stepControl]="borrowerForm" label="Borrower">
      <form [formGroup]="borrowerForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline" class="benchmark-field">
            <mat-label>{{ 'GNR_INDUSTRY' | translate }}</mat-label>
            <mat-select formControlName="industry">
              @for (i of (industries$ | async) ?? []; track i.id) {
                <mat-option [value]="i.id">{{
                  i.description ?? i.label ?? i.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Revenue Size</mat-label>
            <mat-select formControlName="revenueSize">
              @for (r of (revenueSizes$ | async) ?? []; track r.id) {
                <mat-option [value]="r.id">{{
                  r.description ?? r.label ?? r.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>US State or Territory</mat-label>
            <mat-select formControlName="usState">
              @for (s of (usStates$ | async) ?? []; track s.id) {
                <mat-option [value]="s.id">{{
                  s.description ?? s.label ?? s.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>PE Sponsored</mat-label>
            <mat-select formControlName="peSponsored">
              @for (opt of yesNoOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            type="button"
            mat-flat-button
            matStepperNext
            [disabled]="borrowerForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══ Step 4: Pricing ═══ -->
    <mat-step [stepControl]="pricingForm" label="Pricing">
      <form [formGroup]="pricingForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Prepayment Penalty Fee</mat-label>
            <mat-select formControlName="prepayment">
              @for (pp of (prepaymentOptions$ | async) ?? []; track pp.id) {
                <mat-option [value]="pp.id">{{
                  pp.description ?? pp.label ?? pp.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (showExpectedUtilization()) {
            <mat-form-field appearance="outline">
              <mat-label>Expected Utilization</mat-label>
              <mat-select formControlName="expectedUtilization">
                <mat-option value="">-- None --</mat-option>
                @for (
                  eu of (expectedUtilizationOptions$ | async) ?? [];
                  track eu.id
                ) {
                  <mat-option [value]="eu.id">{{
                    eu.description ?? eu.label ?? eu.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>

        @if (!isFloatingRate()) {
          <mat-form-field
            appearance="outline"
            class="full-width benchmark-field">
            <mat-label>Initial Interest Rate</mat-label>
            <input
              matInput
              type="number"
              formControlName="fixedRate"
              min="0"
              step="0.01" />
            <span matTextSuffix>%</span>
          </mat-form-field>
        }

        @if (isFloatingRate()) {
          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline" class="benchmark-field">
              <mat-label>Base Rate Index</mat-label>
              <mat-select formControlName="index">
                @for (ri of (rateIndices$ | async) ?? []; track ri.id) {
                  <mat-option [value]="ri.id">{{
                    ri.description ?? ri.label ?? ri.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="benchmark-field">
              <mat-label>Spread</mat-label>
              <input
                matInput
                type="number"
                formControlName="spread"
                step="0.01" />
              <span matTextSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline">
              <mat-label>Interest Rate Floor Type</mat-label>
              <mat-select formControlName="rateFloorType">
                @for (ft of (rateFloorTypes$ | async) ?? []; track ft.id) {
                  <mat-option [value]="ft.id">{{
                    ft.description ?? ft.label ?? ft.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            @if (showFloorRate()) {
              <mat-form-field appearance="outline">
                <mat-label>Floor Rate</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="floor"
                  step="0.01" />
                <span matTextSuffix>%</span>
              </mat-form-field>
            }
          </div>
        }

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Origination Fee</mat-label>
            <input
              matInput
              type="number"
              formControlName="originationFee"
              min="0"
              step="0.01" />
            <span matTextSuffix>%</span>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Performance-Based Pricing</mat-label>
            <mat-select formControlName="performancePricing">
              @for (opt of yesNoOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            type="button"
            mat-flat-button
            matStepperNext
            [disabled]="pricingForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══ Step 5: Collateral ═══ -->
    <mat-step [stepControl]="collateralForm" label="Collateral">
      <form
        [formGroup]="collateralForm"
        class="step-form"
        [hidden]="showSubStepper">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Recourse Level</mat-label>
            <mat-select formControlName="recourseLevel">
              @for (r of (recourseLevelOptions$ | async) ?? []; track r.id) {
                <mat-option [value]="r.id">{{
                  r.description ?? r.label ?? r.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Guarantor Liability Burndown</mat-label>
            <mat-select formControlName="guarantorBurndown">
              @for (opt of yesNoOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        @if (isFloatingRate()) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Interest Rate Hedge (Derivative)</mat-label>
            <mat-select formControlName="hedge">
              @for (opt of yesNoOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <div class="step-actions" [hidden]="showSubStepper">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          @if (showCreStep()) {
            <button
              type="button"
              mat-flat-button
              matStepperNext
              [disabled]="collateralForm.invalid">
              {{ 'CMD_NEXT' | translate }}
              <mat-icon iconPositionEnd>arrow_forward</mat-icon>
            </button>
          } @else if (subFacilityForms().length === 0) {
            <button
              type="button"
              mat-flat-button
              class="qt-dialog-primary-button"
              (click)="onSubmit()"
              [disabled]="isSaving()">
              @if (isSaving()) {
                Saving...
              } @else {
                {{ 'CMD_SAVE' | translate }}
              }
            </button>
          } @else if (!showSubStepper) {
            <button
              type="button"
              mat-flat-button
              class="qt-dialog-primary-button"
              (click)="enterSubStepper()"
              [disabled]="collateralForm.invalid">
              Add Sub-Facility #1
              <mat-icon iconPositionEnd>arrow_forward</mat-icon>
            </button>
          }
        </div>
      </form>

      @if (showSubStepper && currentSubForm() && !showCreStep()) {
        <ng-container *ngTemplateOutlet="subStepperTemplate"></ng-container>
      }

      @if (showSubStepper && showCreStep()) {
        <div class="sub-stepper-notice">
          <mat-icon class="sub-stepper-notice__icon">check_circle</mat-icon>
          <div class="sub-stepper-notice__content">
            <p class="sub-stepper-notice__title">
              Main facility collateral already completed
            </p>
            <p class="sub-stepper-notice__text">
              Return to the sub-facility flow to continue. Your collateral
              values are saved.
            </p>
          </div>
          <button
            type="button"
            mat-stroked-button
            (click)="returnToSubStepper()">
            <mat-icon>arrow_forward</mat-icon>
            Back to Sub-Facility
          </button>
        </div>
      }
    </mat-step>

    <!-- ═══ Step 6: CRE (conditional) ═══ -->
    @if (showCreStep()) {
      <mat-step [stepControl]="creForm" label="CRE">
        <form [formGroup]="creForm" class="step-form" [hidden]="showSubStepper">
          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline">
              <mat-label>Property Class</mat-label>
              <mat-select formControlName="propertyClass">
                @for (c of (crePropertyClasses$ | async) ?? []; track c.id) {
                  <mat-option [value]="c.id">{{
                    c.description ?? c.label ?? c.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Project Type</mat-label>
              <mat-select formControlName="projectType">
                @for (t of (creProjectTypes$ | async) ?? []; track t.id) {
                  <mat-option [value]="t.id">{{
                    t.description ?? t.label ?? t.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="form-row form-row--3col">
            <mat-form-field appearance="outline">
              <mat-label>LTV</mat-label>
              <input
                matInput
                type="number"
                formControlName="ltv"
                min="0"
                max="100"
                step="0.1" />
              <span matTextSuffix>%</span>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>LTC</mat-label>
              <input
                matInput
                type="number"
                formControlName="ltc"
                min="0"
                max="100"
                step="0.1" />
              <span matTextSuffix>%</span>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Debt Yield</mat-label>
              <input
                matInput
                type="number"
                formControlName="debtYield"
                min="0"
                step="0.01" />
              <span matTextSuffix>%</span>
            </mat-form-field>
          </div>
          <div class="step-actions">
            <button
              type="button"
              mat-stroked-button
              (click)="onCancel()"
              [disabled]="isSaving()">
              {{ 'GNR_CANCEL' | translate }}
            </button>
            <div class="spacer"></div>
            <button type="button" mat-stroked-button matStepperPrevious>
              <mat-icon>arrow_back</mat-icon>
              {{ 'CMD_BACK' | translate }}
            </button>
            @if (subFacilityForms().length === 0) {
              <button
                type="button"
                mat-flat-button
                class="qt-dialog-primary-button"
                (click)="onSubmit()"
                [disabled]="isSaving()">
                @if (isSaving()) {
                  Saving...
                } @else {
                  {{ 'CMD_SAVE' | translate }}
                }
              </button>
            } @else if (!showSubStepper) {
              <button
                type="button"
                mat-flat-button
                class="qt-dialog-primary-button"
                (click)="enterSubStepper()"
                [disabled]="creForm.invalid">
                Add Sub-Facility #1
                <mat-icon iconPositionEnd>arrow_forward</mat-icon>
              </button>
            }
          </div>
        </form>
        @if (showSubStepper && currentSubForm()) {
          <ng-container *ngTemplateOutlet="subStepperTemplate"></ng-container>
        }
      </mat-step>
    }
  </mat-stepper>
</mat-dialog-content>

<!-- ═══ Sub-Stepper Template (shared) ═══ -->
<ng-template #subStepperTemplate>
  <div class="sub-stepper">
    <div class="sub-stepper__progress">
      <div class="sub-stepper__label">
        Sub-Facility {{ currentSubFacility() + 1 }} of
        {{ subFacilityForms().length }}
      </div>
      <div class="sub-stepper__steps">
        @for (
          stepLabel of ['Review', 'Structure', 'Pricing'];
          track stepLabel;
          let si = $index
        ) {
          <div
            class="sub-stepper__step"
            [class.sub-stepper__step--active]="currentSubStep() === si"
            [class.sub-stepper__step--completed]="currentSubStep() > si">
            {{ si + 1 }}
          </div>
          @if (si < 2) {
            <div
              class="sub-stepper__connector"
              [class.sub-stepper__connector--completed]="
                currentSubStep() > si
              "></div>
          }
        }
      </div>
    </div>

    <!-- All sub-steps share single form, sections toggled via [hidden] to preserve values -->
    <form [formGroup]="currentSubForm()" class="step-form">
      <!-- Sub Step 0: Inherited (summary cards with inline editable values) -->
      @if (currentSubStep() === 0) {
        <h3 class="sub-facility-title">
          Sub-Facility #{{ currentSubFacility() + 1 }} — Review Information
        </h3>
        <p class="section-subtitle">
          These values are inherited from the main facility. Edit if needed for
          this sub-facility.
        </p>

        <div class="summary-cards">
          <div class="summary-card">
            <h4>Deal Setup</h4>
            <div class="summary-item">
              <span>Transaction Type:</span>
              <mat-select
                formControlName="transactionType"
                class="inline-select">
                @for (t of (syndicationTypes$ | async) ?? []; track t.id) {
                  <mat-option [value]="t.id">{{
                    t.description ?? t.label ?? t.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
            <div class="summary-item">
              <span>Deal Name:</span>
              <input matInput formControlName="name" class="inline-input" />
            </div>
            <div class="summary-item">
              <span># Lenders:</span>
              <input
                matInput
                type="number"
                formControlName="numLenders"
                min="1"
                class="inline-input" />
            </div>
            <div class="summary-item">
              <span>Loan Purpose:</span>
              <mat-select formControlName="loanPurpose" class="inline-select">
                @for (p of (loanPurposes$ | async) ?? []; track p.id) {
                  <mat-option [value]="p.id">{{
                    p.description ?? p.label ?? p.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
          </div>

          <div class="summary-card">
            <h4>Borrower</h4>
            <div class="summary-item">
              <span>Industry:</span>
              <mat-select formControlName="industry" class="inline-select">
                @for (i of (industries$ | async) ?? []; track i.id) {
                  <mat-option [value]="i.id">{{
                    i.description ?? i.label ?? i.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
            <div class="summary-item">
              <span>Revenue Size:</span>
              <mat-select formControlName="revenueSize" class="inline-select">
                @for (r of (revenueSizes$ | async) ?? []; track r.id) {
                  <mat-option [value]="r.id">{{
                    r.description ?? r.label ?? r.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
            <div class="summary-item">
              <span>PE Sponsored:</span>
              <mat-select formControlName="peSponsored" class="inline-select">
                @for (opt of yesNoOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </div>
            <div class="summary-item">
              <span>State:</span>
              <mat-select formControlName="usState" class="inline-select">
                @for (s of (usStates$ | async) ?? []; track s.id) {
                  <mat-option [value]="s.id">{{
                    s.description ?? s.label ?? s.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
          </div>

          <div class="summary-card">
            <h4>Collateral</h4>
            <div class="summary-item">
              <span>Recourse Level:</span>
              <mat-select formControlName="recourseLevel" class="inline-select">
                @for (r of (recourseLevelOptions$ | async) ?? []; track r.id) {
                  <mat-option [value]="r.id">{{
                    r.description ?? r.label ?? r.id
                  }}</mat-option>
                }
              </mat-select>
            </div>
            <div class="summary-item">
              <span>Guarantor Burndown:</span>
              <mat-select
                formControlName="guarantorBurndown"
                class="inline-select">
                @for (opt of yesNoOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </div>
          </div>
        </div>

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          @if (currentSubFacility() > 0 || currentSubStep() > 0) {
            <button type="button" mat-stroked-button (click)="subStepPrev()">
              <mat-icon>arrow_back</mat-icon>
              {{ 'CMD_BACK' | translate }}
            </button>
          }
          <button type="button" mat-flat-button (click)="subStepNext()">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      }
      @if (currentSubStep() === 1) {
        <h3 class="sub-facility-title">
          Sub-Facility #{{ currentSubFacility() + 1 }} — Loan Structure
        </h3>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Sub Facility Name</mat-label>
          <input
            matInput
            formControlName="name"
            placeholder="e.g., Revolver, Term Loan A" />
        </mat-form-field>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Product Type</mat-label>
            <mat-select formControlName="productType">
              @for (p of (productTypes$ | async) ?? []; track p.id) {
                <mat-option [value]="p.id">{{
                  p.description ?? p.label ?? p.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Interest Rate Type</mat-label>
            <mat-select formControlName="rateType">
              @for (r of (rateTypes$ | async) ?? []; track r.id) {
                <mat-option [value]="r.id">{{
                  r.description ?? r.label ?? r.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Original Loan Amount</mat-label>
          <input matInput type="number" formControlName="amount" min="0" />
          <span matTextPrefix>$&nbsp;</span>
        </mat-form-field>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Term (months)</mat-label>
            <input
              matInput
              type="number"
              formControlName="term"
              min="1"
              (change)="autoCalcSubMaturity(currentSubForm())" />
          </mat-form-field>
          @if (showSubAmortization(currentSubForm())) {
            <mat-form-field appearance="outline">
              <mat-label>Amortization (months)</mat-label>
              <input
                matInput
                type="number"
                formControlName="amortization"
                min="0" />
            </mat-form-field>
          }
        </div>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Closed Date</mat-label>
            <input
              matInput
              [matDatepicker]="subClosedPicker"
              formControlName="closedDate"
              (dateChange)="autoCalcSubMaturity(currentSubForm())" />
            <mat-datepicker-toggle
              matSuffix
              [for]="subClosedPicker"></mat-datepicker-toggle>
            <mat-datepicker #subClosedPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Maturity Date</mat-label>
            <input
              matInput
              [matDatepicker]="subMaturityPicker"
              formControlName="maturityDate" />
            <mat-datepicker-toggle
              matSuffix
              [for]="subMaturityPicker"></mat-datepicker-toggle>
            <mat-datepicker #subMaturityPicker></mat-datepicker>
            <mat-hint>Auto-calculated from closed date + term</mat-hint>
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button (click)="subStepPrev()">
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button type="button" mat-flat-button (click)="subStepNext()">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      }

      @if (currentSubStep() === 2) {
        <h3 class="sub-facility-title">
          Sub-Facility #{{ currentSubFacility() + 1 }} — Pricing
        </h3>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Prepayment Penalty</mat-label>
            <mat-select formControlName="prepayment">
              @for (pp of (prepaymentOptions$ | async) ?? []; track pp.id) {
                <mat-option [value]="pp.id">{{
                  pp.description ?? pp.label ?? pp.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          @if (!isSubTermLoan(currentSubForm())) {
            <mat-form-field appearance="outline">
              <mat-label>Expected Utilization</mat-label>
              <mat-select formControlName="expectedUtilization">
                <mat-option value="">-- None --</mat-option>
                @for (
                  eu of (expectedUtilizationOptions$ | async) ?? [];
                  track eu.id
                ) {
                  <mat-option [value]="eu.id">{{
                    eu.description ?? eu.label ?? eu.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>

        @if (!isSubFloatingRate(currentSubForm())) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Initial Interest Rate</mat-label>
            <input
              matInput
              type="number"
              formControlName="fixedRate"
              step="0.01" />
            <span matTextSuffix>%</span>
          </mat-form-field>
        }

        @if (isSubFloatingRate(currentSubForm())) {
          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline">
              <mat-label>Base Rate Index</mat-label>
              <mat-select formControlName="index">
                @for (ri of (rateIndices$ | async) ?? []; track ri.id) {
                  <mat-option [value]="ri.id">{{
                    ri.description ?? ri.label ?? ri.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Spread</mat-label>
              <input
                matInput
                type="number"
                formControlName="spread"
                step="0.01" />
              <span matTextSuffix>%</span>
            </mat-form-field>
          </div>
          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline">
              <mat-label>Floor Type</mat-label>
              <mat-select formControlName="rateFloorType">
                @for (ft of (rateFloorTypes$ | async) ?? []; track ft.id) {
                  <mat-option [value]="ft.id">{{
                    ft.description ?? ft.label ?? ft.id
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (showSubFloorRate(currentSubForm())) {
              <mat-form-field appearance="outline">
                <mat-label>Floor Rate</mat-label>
                <input
                  matInput
                  type="number"
                  formControlName="floor"
                  step="0.01" />
                <span matTextSuffix>%</span>
              </mat-form-field>
            }
          </div>
        }

        <div class="step-actions">
          <button
            type="button"
            mat-stroked-button
            (click)="onCancel()"
            [disabled]="isSaving()">
            {{ 'GNR_CANCEL' | translate }}
          </button>
          <div class="spacer"></div>
          <button type="button" mat-stroked-button (click)="subStepPrev()">
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          @if (isLastSubStep) {
            <button
              type="button"
              mat-flat-button
              class="qt-dialog-primary-button"
              (click)="onSubmit()"
              [disabled]="isSaving()">
              @if (isSaving()) {
                Saving...
              } @else {
                {{ 'CMD_SAVE' | translate }}
              }
            </button>
          } @else {
            <button type="button" mat-flat-button (click)="subStepNext()">
              {{ 'CMD_NEXT' | translate }}
              <mat-icon iconPositionEnd>arrow_forward</mat-icon>
            </button>
          }
        </div>
      }
    </form>
  </div>
</ng-template>
